import * as Sentry from '@sentry/react';
import { env } from '@/utils/env';
import packageJson from '../../package.json';

/**
 * Initialize Sentry error tracking
 */
export function initSentry() {
  // Skip initialization entirely in development to avoid "Transport disabled" warnings
  if (env.isDevelopment) {
    return;
  }

  if (!env.SENTRY_DSN) {
    console.warn('Sentry DSN not configured, error tracking disabled');
    return;
  }

  Sentry.init({
    dsn: env.SENTRY_DSN,
    environment: env.ENV,
    release: `${packageJson.name}@${packageJson.version}`,
    enabled: env.isProduction || env.ENV === 'staging',
    debug: false,

    // Performance monitoring - reduced to 1% in production, 10% in staging
    tracesSampleRate: env.isProduction ? 0.01 : 0.1,

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Session replay sampling - reduced to 1% in production, 10% in staging
    replaysSessionSampleRate: env.isProduction ? 0.01 : 0.1,
    // Only capture replays for 10% of errors (down from 100%)
    replaysOnErrorSampleRate: 0.1,

    // Filter out expected errors
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Don't send network errors in dev
      if (env.isDevelopment && error instanceof Error) {
        if (error.message.includes('Network Error')) {
          return null;
        }
      }

      return event;
    },
  });
}

/**
 * Add breadcrumb for tracking user actions
 */
export function addBreadcrumb(
  category: string,
  message: string,
  level: Sentry.SeverityLevel = 'info',
  data?: Record<string, unknown>
) {
  Sentry.addBreadcrumb({
    category,
    message,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
}

/**
 * Capture exception manually
 */
export function captureException(
  error: Error,
  context?: Record<string, unknown>
) {
  Sentry.captureException(error, {
    contexts: context ? { custom: context } : undefined,
  });
}
